import React, { useState, useCallback, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { Search, Calendar, Clock, Car, Phone, XCircle, CalendarPlus, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const businessHours = {
  weekday: ['2:00 PM', '4:00 PM', '6:00 PM'],
  weekend: ['9:00 AM', '11:00 AM', '1:00 PM', '3:00 PM', '5:00 PM']
};

const statusColors = {
  pending: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  confirmed: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  completed: 'bg-green-500/15 text-green-400 border border-green-500/30',
  cancelled: 'bg-red-500/15 text-red-400 border border-red-500/30',
};

const MyAppointments = () => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const lookupBookings = async (e) => {
    e.preventDefault();
    if (!phone) {
      toast.error('Please enter your phone number');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/bookings/lookup?phone=${encodeURIComponent(phone)}`);
      setBookings(res.data);
      if (res.data.length === 0) {
        toast.info('No appointments found for this phone number');
      }
    } catch {
      toast.error('Failed to look up appointments');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookedSlots = useCallback(async (date) => {
    if (!date) return;
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/bookings/booked-slots?date=${date}`);
      setBookedSlots(res.data.map(s => s.time));
    } catch {
      setBookedSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (newDate) fetchBookedSlots(newDate);
  }, [newDate, fetchBookedSlots]);

  const getAvailableTimeSlots = () => {
    if (!newDate) return [];
    const parts = newDate.split('-');
    const selectedDate = new Date(parts[0], parts[1] - 1, parts[2]);
    const dayOfWeek = selectedDate.getDay();
    return (dayOfWeek === 0 || dayOfWeek === 6) ? businessHours.weekend : businessHours.weekday;
  };

  const handleReschedule = async (bookingId) => {
    if (!newDate || !newTime) {
      toast.error('Please select a new date and time');
      return;
    }
    setActionLoading(bookingId);
    try {
      await axios.post(`${BACKEND_URL}/api/bookings/${bookingId}/reschedule`, {
        phone, date: newDate, time: newTime
      });
      toast.success('Appointment rescheduled successfully!');
      setRescheduleId(null);
      setNewDate('');
      setNewTime('');
      const res = await axios.get(`${BACKEND_URL}/api/bookings/lookup?phone=${encodeURIComponent(phone)}`);
      setBookings(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setActionLoading('');
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this appointment?')) return;
    setActionLoading(bookingId);
    try {
      await axios.post(`${BACKEND_URL}/api/bookings/${bookingId}/cancel`, { phone });
      toast.success('Appointment cancelled');
      const res = await axios.get(`${BACKEND_URL}/api/bookings/lookup?phone=${encodeURIComponent(phone)}`);
      setBookings(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setActionLoading('');
    }
  };

  const downloadICS = (b) => {
    const match = b.time.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!match) return;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    if (match[3].toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (match[3].toUpperCase() === 'AM' && hours === 12) hours = 0;
    const [y, m, d] = b.date.split('-').map(Number);
    const start = new Date(y, m - 1, d, hours, minutes);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    const fmt = (dt) => dt.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
      `DTSTART:${fmt(start)}`, `DTEND:${fmt(end)}`,
      `SUMMARY:PDX Window Tint - ${b.service}`,
      `DESCRIPTION:Service: ${b.service}\\nDROP-OFF ONLY\\nCall 971.249.2752`,
      `LOCATION:PDX Window Tint, Portland, OR`,
      'END:VEVENT', 'END:VCALENDAR'
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'pdx-appointment.ics';
    a.click();
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-2xl mx-auto px-4 pt-10 pb-16">
        {/* Back arrow */}
        <button
          onClick={() => navigate('/')}
          data-testid="back-to-home-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#a1a1aa', fontSize: '14px', fontWeight: 600,
            marginBottom: '24px', padding: '0',
          }}
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={2.5} />
          Back
        </button>
        <h1 className="text-3xl font-bold text-white mb-1" data-testid="appointments-heading">My Appointments</h1>
        <p className="text-zinc-500 text-sm mb-8">Look up, reschedule, or cancel your bookings</p>

        {/* Phone Lookup */}
        <form onSubmit={lookupBookings} className="flex gap-3 mb-8" data-testid="phone-lookup-form">
          <div className="flex-1">
            <Input
              data-testid="lookup-phone-input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Enter your phone number"
              className="bg-zinc-950 border-zinc-900 text-white h-12"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="lookup-submit-btn"
            className="h-12 px-6 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold uppercase text-sm"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </form>

        {/* Results */}
        {searched && !loading && bookings.length === 0 && (
          <div className="text-center py-12" data-testid="no-appointments-found">
            <AlertCircle className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">No appointments found for this phone number.</p>
          </div>
        )}

        <div className="space-y-4" data-testid="bookings-list">
          {bookings.map((b) => (
            <div key={b.id} className="bg-zinc-950 border border-zinc-800 rounded-xl p-5" data-testid={`booking-card-${b.id}`}>
              {/* Status Badge */}
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold uppercase px-3 py-1 rounded-full ${statusColors[b.status] || statusColors.pending}`}>
                  {b.status}
                </span>
                <span className="text-zinc-600 text-xs">{b.created_at?.slice(0, 10)}</span>
              </div>

              {/* Booking Details */}
              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-zinc-500 flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Service</span>
                  <span className="text-amber-500 font-semibold">{b.service}</span>
                </div>
                {b.package && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Package</span>
                    <span className="text-white">{b.package}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-zinc-500 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Date</span>
                  <span className="text-white font-medium">{b.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Time</span>
                  <span className="text-white font-medium">{b.time}</span>
                </div>
                {b.vehicle_info && (
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Vehicle</span>
                    <span className="text-white">{b.vehicle_info}</span>
                  </div>
                )}
              </div>

              {/* Reschedule Form */}
              {rescheduleId === b.id && (
                <div className="bg-zinc-900 rounded-lg p-4 mb-4 space-y-3" data-testid={`reschedule-form-${b.id}`}>
                  <p className="text-white text-sm font-semibold">Pick a new date & time</p>
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <Label className="text-zinc-400 text-xs">Date</Label>
                      <Input
                        type="date"
                        value={newDate}
                        onChange={(e) => { setNewDate(e.target.value); setNewTime(''); }}
                        className="bg-zinc-950 border-zinc-800 text-white text-sm w-full h-11 py-2 px-3 appearance-none"
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="reschedule-date-input"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <Label className="text-zinc-400 text-xs">
                        Time {loadingSlots && <Loader2 className="w-3 h-3 inline ml-1 animate-spin" />}
                      </Label>
                      <Select value={newTime} onValueChange={setNewTime} disabled={!newDate}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white text-sm w-full" data-testid="reschedule-time-select">
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-950 border-zinc-800">
                          {getAvailableTimeSlots().map((time) => (
                            <SelectItem
                              key={time}
                              value={time}
                              disabled={bookedSlots.includes(time)}
                              className={bookedSlots.includes(time) ? 'text-zinc-600 line-through opacity-50' : 'text-white'}
                            >
                              {time}{bookedSlots.includes(time) ? ' (Booked)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleReschedule(b.id)}
                      disabled={actionLoading === b.id}
                      className="flex-1 h-9 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs"
                      data-testid="reschedule-confirm-btn"
                    >
                      {actionLoading === b.id ? 'Saving...' : 'Confirm Reschedule'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setRescheduleId(null); setNewDate(''); setNewTime(''); }}
                      className="h-9 rounded-lg border-zinc-700 text-white text-xs"
                      data-testid="reschedule-cancel-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {b.status !== 'cancelled' && b.status !== 'completed' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => { setRescheduleId(rescheduleId === b.id ? null : b.id); setNewDate(''); setNewTime(''); }}
                    className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg border border-zinc-700 hover:border-amber-500/50 text-white hover:text-amber-500 text-xs font-medium transition-all"
                    data-testid={`reschedule-btn-${b.id}`}
                  >
                    <CalendarPlus className="w-3.5 h-3.5" />
                    Reschedule
                  </button>
                  <button
                    onClick={() => handleCancel(b.id)}
                    disabled={actionLoading === b.id}
                    className="flex-1 flex items-center justify-center gap-1.5 h-9 rounded-lg border border-zinc-700 hover:border-red-500/50 text-white hover:text-red-400 text-xs font-medium transition-all"
                    data-testid={`cancel-btn-${b.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Cancel
                  </button>
                  <button
                    onClick={() => downloadICS(b)}
                    className="flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 hover:border-amber-500/50 text-white hover:text-amber-500 text-xs font-medium transition-all"
                    data-testid={`calendar-btn-${b.id}`}
                  >
                    <CalendarPlus className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

export default MyAppointments;
